/**
 * Formatters compartilhados pelas páginas FinOps Wave 2.
 */

import type { FinopsSeverity } from '@/types/finops'

const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const MONTH_NAMES_EN = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

/** Formata valor em USD ("$1,234.56"). Retorna em-dash quando null/undefined. */
export function formatUSD(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return USD_FORMATTER.format(value)
}

/** Opções de formatação de percentual. */
export interface FormatPctOptions {
  /**
   * Quando `true`, prefixa `+` em valores positivos (negativos já trazem `-`).
   * Usado para deltas MoM/YoY (ex.: `+12.3%`). Default: `false`.
   */
  signed?: boolean
}

/**
 * Formata percentual ("12.3%"). Retorna em-dash quando null/undefined.
 * `{ signed: true }` prefixa `+` em positivos (deltas MoM/YoY).
 */
export function formatPct(
  value: number | null | undefined,
  opts: FormatPctOptions = {},
): string {
  if (value == null || !Number.isFinite(value)) return '—'
  const sign = opts.signed && value > 0 ? '+' : ''
  return `${sign}${value.toFixed(1)}%`
}

/** Converte "2026-05" → "May/26". Devolve a string original se formato inválido. */
export function formatMonthShort(month: string): string {
  if (!month || month.length < 7) return month
  const [year, mm] = month.split('-')
  const idx = Number.parseInt(mm, 10) - 1
  const monthName = Number.isNaN(idx) ? mm : MONTH_NAMES_EN[idx] ?? mm
  return `${monthName}/${year.slice(-2)}`
}

/** Cor do Mantine `Badge` para cada severity. */
export function severityColor(severity: FinopsSeverity | string): string {
  switch (severity) {
    case 'high':
      return 'red'
    case 'medium':
      return 'yellow'
    case 'low':
      return 'blue'
    default:
      return 'gray'
  }
}

/** Label legível em PT-BR para severity. */
export function severityLabel(severity: FinopsSeverity | string): string {
  switch (severity) {
    case 'high':
      return 'Alta'
    case 'medium':
      return 'Média'
    case 'low':
      return 'Baixa'
    default:
      return severity
  }
}
